/*!
 * Related products (Search & Discovery) for the cart drawer.
 *
 * Fetches /recommendations/products with intent=related for every unique
 * product in the cart, merges and dedupes the results, then renders compact
 * cross-sell cards into a Swiper slider (same library + CDN as the theme's
 * other custom sliders). Re-fetches whenever the cart changes.
 *
 * The drawer is rebuilt via AJAX (theme.buildCart), which only swaps the
 * [data-products] node, so this custom element survives. To stay in sync we
 * re-init from three sources:
 *   - MutationObserver on the [data-products] cart items container
 *   - the theme's cart:updated event
 *   - the ajaxProduct:added event (dispatched by this element's own
 *     add-to-cart buttons and by the theme's product forms)
 *
 * Add-to-cart posts to /cart/add via the theme's native routes and
 * dispatches ajaxProduct:added so the drawer rebuilds itself.
 */
if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.bindEvents();
        this.observeCartItems();
        this.addEventListener('click', this.onTrackClick.bind(this));
        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.unbindEvents();
        this.cartObserver?.disconnect();
        this.destroySwiper();
      }

      bindEvents() {
        this.onCartUpdated = this.refresh.bind(this);
        document.addEventListener('cart:updated', this.onCartUpdated);
        document.addEventListener('ajaxProduct:added', this.onCartUpdated);
      }

      unbindEvents() {
        document.removeEventListener('cart:updated', this.onCartUpdated);
        document.removeEventListener('ajaxProduct:added', this.onCartUpdated);
      }

      // theme.buildCart() swaps the [data-products] node in place. Watching it
      // guarantees the slider re-inits even when no cart event is dispatched.
      observeCartItems() {
        const products = document.querySelector('[data-products]');
        if (!products) return;

        this.cartObserver = new MutationObserver(() => this.refresh());
        this.cartObserver.observe(products, {
          childList: true,
          subtree: true,
        });
      }

      destroySwiper() {
        if (this._swiper) {
          this._swiper.destroy(true, true);
          this._swiper = null;
        }
        if (this._swiperRetry) {
          clearTimeout(this._swiperRetry);
          this._swiperRetry = null;
        }
      }

      // The empty-state block is only displayed while the cart is empty and the
      // inner block only while it has items. A drawer is position:fixed, so
      // offsetParent is always null inside it — check the computed display of
      // the relevant container instead.
      isStateVisible() {
        const container = this.closest('.drawer__cart-empty, .drawer__inner');
        return container ? getComputedStyle(container).display !== 'none' : true;
      }

      // Cart items can carry related products tied to different parent products
      // (Search & Discovery is configured per product). One recommendations
      // request only reflects one product, so every unique product in the cart
      // gets its own request; results are merged and deduped by product id.
      //
      // When the cart is empty there is no seed product for intent=related. If
      // a data-fallback-collection is set (empty cart state), a curated
      // collection is fetched instead via its public products.json endpoint
      // (works on any storefront context; ?section_id= renders are restricted
      // to sections present on the page) and grid cards are built in JS.
      async loadRecommendations() {
        const productIds = (this.dataset.productIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        const track = this.querySelector(
          '.cart-drawer__recommendations-track, .cart-drawer__empty-recommendations-track'
        );
        const wrapper = this.querySelector('.swiper-wrapper');
        const inner = this.querySelector(
          '.cart-drawer__recommendations-inner, .cart-drawer__empty-recommendations-inner'
        );
        if (!track || !wrapper || !inner) return;

        let cards = [];

        if (this.dataset.fallbackCollection) {
          // Empty-state instance: only renders the fallback collection, and only
          // while the empty state is actually on screen.
          if (productIds.length || !this.isStateVisible()) return;
          const data = await fetch(
            `/collections/${this.dataset.fallbackCollection}/products.json?limit=4`,
            { cache: 'no-store' }
          )
            .then((response) => response.json())
            .catch(() => null);
          cards = this.buildFallbackCards(data);
        } else if (productIds.length) {
          const responses = await Promise.all(
            productIds.map((id) =>
              fetch(`${this.dataset.url}&product_id=${id}&section_id=${this.dataset.sectionId}`, {
                cache: 'no-store',
              })
                .then((response) => response.text())
                .catch(() => '')
            )
          );

          const seenProductIds = new Set();
          responses.forEach((text) => {
            if (!text) return;
            const html = document.createElement('div');
            html.innerHTML = text;
            html.querySelectorAll('.cart-recommendation-card').forEach((card) => {
              const idNode = card.matches('[data-variant-id]')
                ? card
                : card.querySelector('[data-variant-id]');
              const cardProductId = idNode?.dataset.variantId;
              if (!cardProductId || seenProductIds.has(cardProductId)) return;
              seenProductIds.add(cardProductId);
              cards.push(card);
            });
          });
        } else {
          return;
        }

        this.renderCards(cards, track, wrapper, inner);
      }

      buildFallbackCards(data) {
        const cards = [];
        if (!data || !Array.isArray(data.products)) return cards;

        data.products.forEach((product) => {
          const variant = product.variants && product.variants[0];
          const productUrl = `/products/${product.handle}`;

          const card = document.createElement('div');
          card.className = 'cart-recommendation-card cart-recommendation-card--grid';
          if (variant) card.dataset.variantId = variant.id;

          const media = document.createElement('a');
          media.href = productUrl;
          media.className = 'cart-recommendation-card__media';
          media.tabIndex = -1;
          media.setAttribute('aria-hidden', 'true');
          media.draggable = false;

          if (product.images && product.images[0]) {
            const img = document.createElement('img');
            img.src = product.images[0].src;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 120;
            img.height = 120;
            img.className = 'cart-recommendation-card__image';
            img.draggable = false;
            media.appendChild(img);
          }
          card.appendChild(media);

          const info = document.createElement('div');
          info.className = 'cart-recommendation-card__info';

          const name = document.createElement('a');
          name.href = productUrl;
          name.className = 'cart-recommendation-card__name';
          name.textContent = product.title;
          info.appendChild(name);

          const priceRow = document.createElement('div');
          priceRow.className = 'cart-recommendation-card__price-row';

          if (
            variant &&
            variant.compare_at_price &&
            Number(variant.compare_at_price) > Number(variant.price)
          ) {
            const oldPrice = document.createElement('span');
            oldPrice.className = 'cart-recommendation-card__old-price';
            oldPrice.textContent = this.formatMoney(variant.compare_at_price);
            priceRow.appendChild(oldPrice);
          }

          const price = document.createElement('span');
          price.className = 'cart-recommendation-card__price';
          price.textContent = variant ? this.formatMoney(variant.price) : '';
          priceRow.appendChild(price);

          info.appendChild(priceRow);
          card.appendChild(info);
          cards.push(card);
        });

        return cards;
      }

      formatMoney(value) {
        const cents = Math.round(parseFloat(value) * 100);
        if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
          return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        }
        return `$${value}`;
      }

      renderCards(cards, track, wrapper, inner) {
        this.destroySwiper();
        wrapper.innerHTML = '';

        if (!cards.length) {
          inner.setAttribute('hidden', '');
          return;
        }

        cards.forEach((card) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide cart-drawer__recommendations-slide';
          slide.appendChild(card);
          wrapper.appendChild(slide);
        });

        this.initSwiper(track, inner);
      }

      initSwiper(track, inner) {
        if (typeof Swiper === 'undefined') {
          this._swiperRetry = setTimeout(() => this.initSwiper(track, inner), 100);
          return;
        }

        const desktopSlides = parseFloat(this.dataset.slidesPerView) || 2.1;
        const mobileSlides = parseFloat(this.dataset.slidesPerViewMobile) || 1.5;

        this._swiper = new Swiper(track, {
          slidesPerView: mobileSlides,
          slidesPerGroup: 1,
          spaceBetween: 12,
          grabCursor: true,
          watchOverflow: true,
          speed: 400,
          threshold: 6,
          breakpoints: {
            750: {
              slidesPerView: desktopSlides,
            },
          },
        });

        inner.removeAttribute('hidden');

        requestAnimationFrame(() => {
          if (this._swiper) this._swiper.update();
        });
      }

      onTrackClick(event) {
        const btn = event.target.closest('[data-add-to-cart]');
        if (!btn) return;

        const variantId = btn.dataset.variantId;
        if (!variantId || !window.theme || !theme.routes || !theme.routes.cartAdd) return;

        btn.disabled = true;

        const body = new URLSearchParams({
          id: variantId,
          quantity: 1,
        });

        fetch(theme.routes.cartAdd, {
          method: 'POST',
          body: body.toString(),
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status === 422) {
              btn.disabled = false;
              return;
            }
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: { product: data, addToCartBtn: btn },
              })
            );
          })
          .catch(() => {
            btn.disabled = false;
          });
      }

      refresh() {
        if (!window.theme || !theme.cart || !theme.cart.getCart) return;
        if (this._refreshing) return;

        this._refreshing = true;

        theme.cart
          .getCart()
          .then((cart) => {
            this._refreshing = false;

            const ids = [...new Set(cart.items.map((item) => item.product_id))].join(',');
            if (ids === this.dataset.productIds) return;

            this.dataset.productIds = ids;
            this.loadRecommendations();
          })
          .catch(() => {
            this._refreshing = false;
          });
      }
    }
  );
}